import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { useFavorites } from '@/hooks/useFavorites';
import {
  getComicById,
  getRelatedComics,
  getCategoryById,
  buildChapters,
  formatPopularity,
} from '@/lib/comics';
import ChapterList from '@/pages/detail/components/ChapterList';
import ReadModal from '@/pages/detail/components/ReadModal';
import ComicCard from '@/components/base/ComicCard';
import type { Chapter } from '@/types/comic';

export default function DetailPage() {
  const { id = '' } = useParams();
  const comic = getComicById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [modalChapter, setModalChapter] = useState<Chapter | null>(null);

  useSEO({
    title: comic ? `${comic.title} - 韩漫漫画详情` : '漫画详情 - 韩漫',
    description: comic
      ? `${comic.description.slice(0, 90)} 韩漫《${comic.title}》由${comic.author}创作，${comic.status}中。`
      : '韩漫漫画详情页。',
    keywords: comic
      ? `${comic.title},韩漫,${comic.categories.map((c) => getCategoryById(c)?.name).filter(Boolean).join(',')},漫画推荐`
      : '韩漫,漫画详情',
    path: `/detail/${id}`,
  });

  if (!comic) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </div>
        <h1 className="mt-5 font-heading font-bold text-2xl text-foreground-950">未找到该漫画</h1>
        <p className="mt-2 text-sm text-foreground-600">作品可能已下线，或链接有误。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const chapters = buildChapters(comic);
  const related = getRelatedComics(comic, 4);
  const favorited = isFavorite(comic.id);
  const primaryCategory = getCategoryById(comic.categories[0]);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8 py-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        {primaryCategory && (
          <>
            <Link
              to={`/category/${primaryCategory.id}`}
              className="hover:text-primary-600 transition-colors cursor-pointer"
            >
              {primaryCategory.name}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
          </>
        )}
        <span className="text-foreground-800 font-medium truncate max-w-[200px]">{comic.title}</span>
      </nav>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 md:gap-8">
        <div className="w-[180px] md:w-full mx-auto md:mx-0">
          <div className="w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-200 bg-background-200">
            <img
              src={comic.cover}
              alt={`${comic.title} 韩漫漫画封面`}
              title={`${comic.title} 韩漫漫画在线看`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <span
              className={`px-2.5 py-1 rounded-md text-xs font-label font-medium ${
                comic.status === '连载' ? 'bg-primary-500 text-background-50' : 'bg-accent-600 text-background-50'
              }`}
            >
              {comic.status}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-label">
              更新至 {comic.latestChapter}
            </span>
          </div>

          <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">{comic.title}</h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {comic.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-star-fill text-primary-400"></i>
              {comic.rating.toFixed(1)} 分
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line text-accent-500"></i>
              {formatPopularity(comic.popularity)} 热度
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {comic.updateDate} 更新
            </span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {comic.categories.map((cid) => {
              const c = getCategoryById(cid);
              if (!c) return null;
              return (
                <Link
                  key={cid}
                  to={`/category/${cid}`}
                  className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-label hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {c.name}
                </Link>
              );
            })}
            {comic.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 rounded-full bg-background-200 text-foreground-700 text-xs font-label">
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setModalChapter(chapters[0])}
              className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              开始阅读
            </button>
            <button
              type="button"
              onClick={() => toggleFavorite(comic.id)}
              className={`inline-flex items-center gap-2 px-5 h-11 rounded-md border text-sm md:text-base font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
                favorited
                  ? 'bg-primary-50 border-primary-300 text-primary-600'
                  : 'border-background-300 text-foreground-700 hover:bg-background-100'
              }`}
            >
              <i className={`${favorited ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
              {favorited ? '已收藏' : '收藏'}
            </button>
            {primaryCategory && (
              <Link
                to={`/category/${primaryCategory.id}`}
                className="inline-flex items-center gap-2 px-5 h-11 rounded-md border border-background-300 text-foreground-700 text-sm md:text-base font-label font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-go-back-line text-lg"></i>
                返回分类
              </Link>
            )}
          </div>

          <section className="mt-8">
            <h2 className="font-heading font-bold text-xl text-foreground-950">作品简介</h2>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">{comic.description}</p>
          </section>
        </div>
      </div>

      <div className="mt-8">
        <ChapterList chapters={chapters} onSelect={setModalChapter} />
      </div>

      <section className="mt-10">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5 flex items-center gap-2">
          <i className="ri-thumb-up-line text-primary-500"></i>
          相关推荐
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {related.map((item) => (
            <ComicCard key={item.id} comic={item} showAuthor />
          ))}
        </div>
      </section>

      <ReadModal open={Boolean(modalChapter)} chapterTitle={modalChapter?.title ?? ''} onClose={() => setModalChapter(null)} />
    </div>
  );
}